import {
  useState,
  useEffect,
  useCallback,
  createContext,
  useContext,
} from "react";
import * as authService from "../services/authService";
import { isCapacitorApp } from "../utils/subscriptionHandler";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Initialize auth - handle OAuth callback or restore existing session
  useEffect(() => {
    const initialize = async () => {
      // Check for OAuth callback parameters first
      if (typeof window !== "undefined") {
        const urlParams = new URLSearchParams(window.location.search);
        const accessToken = urlParams.get("access_token");
        const refreshToken = urlParams.get("refresh_token");
        const authSuccess = urlParams.get("auth_success");
        const authError = urlParams.get("auth_error");

        if (authError) {
          setError("Google authentication failed: " + authError);
          window.history.replaceState(
            {},
            document.title,
            window.location.pathname,
          );
          setLoading(false);
          return;
        }

        // Handle OAuth success - process callback tokens
        if (authSuccess && accessToken && refreshToken) {
          try {
            await handleGoogleCallback(accessToken, refreshToken);
          } catch (error) {
            console.error("Failed to process Google callback:", error);
          } finally {
            // Always clean URL params regardless of success/failure
            window.history.replaceState(
              {},
              document.title,
              window.location.pathname,
            );
          }
          return;
        }
      }

      // No OAuth callback - restore existing session
      try {
        const currentUser = authService.getCurrentUser();
        if (currentUser && authService.isAuthenticated()) {
          const profile = await authService.getUserProfile();
          setUser(profile);
        }
      } catch (error) {
        console.warn("Failed to restore authentication:", error);
        await authService.logout();
      } finally {
        setLoading(false);
      }
    };

    initialize();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Listen for deep link OAuth callbacks on mobile (Capacitor)
  useEffect(() => {
    if (!isCapacitorApp()) return;

    let listener;
    const setupDeepLinkListener = async () => {
      try {
        const { App } = await import("@capacitor/app");
        const { Browser } = await import("@capacitor/browser");

        listener = await App.addListener("appUrlOpen", async (event) => {
          try {
            const url = new URL(event.url);

            // Handle OAuth callback deep link (custom scheme or https:// intercepted by Android)
            if (
              (url.protocol === "lyricaltoolkit:" && url.hostname === "auth") ||
              (url.protocol === "https:" && url.searchParams.has("auth_success"))
            ) {
              const accessToken = url.searchParams.get("access_token");
              const refreshToken = url.searchParams.get("refresh_token");
              const authSuccess = url.searchParams.get("auth_success");
              const authError = url.searchParams.get("auth_error");

              // Close the browser that was opened for OAuth
              try {
                await Browser.close();
              } catch (e) {
                // Browser may already be closed
              }

              if (authError) {
                setError("Google authentication failed: " + authError);
                return;
              }

              if (authSuccess && accessToken && refreshToken) {
                await handleGoogleCallback(accessToken, refreshToken);
              }
            }
          } catch (err) {
            console.error("Failed to process deep link:", err);
            setError("Authentication failed");
          }
        });
      } catch (e) {
        console.warn("Deep link listener setup failed:", e);
      }
    };

    setupDeepLinkListener();

    return () => {
      if (listener) {
        listener.remove();
      }
    };
  }, []); // Only run once on mount

  const login = async (loginValue, password) => {
    try {
      setError(null);
      setLoading(true);
      const result = await authService.login(loginValue, password);
      setUser(result.user);
      return result;
    } catch (error) {
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const signup = async (username, email, password) => {
    try {
      setError(null);
      setLoading(true);
      const result = await authService.signup(username, email, password);
      setUser(result.user);
      return result;
    } catch (error) {
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await authService.logout();
      setUser(null);
      setError(null);
    } catch (error) {
      console.warn("Logout error:", error);
      // Force clear state even if server request fails
      setUser(null);
      setError(null);
    }
  };

  const verifyEmail = async (token) => {
    try {
      setError(null);
      const result = await authService.verifyEmail(token);
      // Update user verification status (both shapes — different endpoints
      // serialize the field with different names)
      if (user) {
        setUser({ ...user, emailVerified: true, email_verified: true });
      }
      // Persist updated user so a reload keeps the verified state
      try {
        const stored = JSON.parse(localStorage.getItem("currentUser") || "null");
        if (stored) {
          stored.emailVerified = true;
          stored.email_verified = true;
          localStorage.setItem("currentUser", JSON.stringify(stored));
        }
      } catch (e) {
        // ignore storage errors
      }
      return result;
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  const resendVerificationEmail = async () => {
    try {
      setError(null);
      return await authService.resendVerificationEmail();
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  const requestPasswordReset = async (email) => {
    try {
      setError(null);
      return await authService.requestPasswordReset(email);
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  const resetPassword = async (token, newPassword) => {
    try {
      setError(null);
      return await authService.resetPassword(token, newPassword);
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  const refreshProfile = async () => {
    try {
      if (authService.isAuthenticated()) {
        const profile = await authService.getUserProfile();
        setUser(profile);
        return profile;
      }
    } catch (error) {
      console.warn("Failed to refresh profile:", error);
      // If profile refresh fails, user might need to login again
      if (error.message.includes("expired")) {
        await logout();
      }
    }
  };

  const clearError = () => setError(null);

  // Google OAuth methods
  const initiateGoogleAuth = async () => {
    try {
      setError(null);
      await authService.initiateGoogleAuth();
      // User will be redirected to Google
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  const handleGoogleCallback = async (accessToken, refreshToken) => {
    try {
      setLoading(true);
      setError(null);
      const user = await authService.handleGoogleCallback(
        accessToken,
        refreshToken,
      );
      setUser(user);

      // Fetch full profile
      await refreshProfile();

      return user;
    } catch (error) {
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const linkGoogleAccount = async (googleId, googleEmail) => {
    try {
      setError(null);
      const result = await authService.linkGoogleAccount(googleId, googleEmail);
      setUser(result.user);
      return result;
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  const unlinkGoogleAccount = async () => {
    try {
      setError(null);
      const result = await authService.unlinkGoogleAccount();
      setUser(result.user);
      return result;
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  const value = {
    user,
    loading,
    error,
    isAuthenticated: !!user,
    login,
    signup,
    logout,
    verifyEmail,
    resendVerificationEmail,
    requestPasswordReset,
    resetPassword,
    refreshProfile,
    clearError,
    initiateGoogleAuth,
    handleGoogleCallback,
    linkGoogleAccount,
    unlinkGoogleAccount,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export default useAuth;
